import i18next from 'i18next'
import qs from 'query-string'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToasts } from 'react-toast-notifications'
import { EmbedNextAction } from 'src/applications/Embed/components/EmbedNextAction'
import { FeatureFlagContext } from 'src/applications/Embed/fragments/FeatureFlagContext'
import { Loader } from 'src/applications/Embed/fragments/Loader'
import { DefaultTheme, themed, ThemeWrapper } from 'src/applications/Embed/fragments/ThemeWrapper'
import { InitialScene } from 'src/applications/Embed/scenes/1_InitialScene'
import { DataSourceScene } from 'src/applications/Embed/scenes/2_DataSourceScene'
import { HeaderRowSelectionScene } from 'src/applications/Embed/scenes/3_HeaderRowSelectionScene'
import { MatchingScene } from 'src/applications/Embed/scenes/4_MatchingScene'
import { ReviewScene } from 'src/applications/Embed/scenes/5_ReviewScene'
import { ErrorScene } from 'src/applications/Embed/scenes/6_ErrorScene'
import { FinalScene } from 'src/applications/Embed/scenes/7_FinalScene'
import { IBatch } from 'src/applications/Embed/types/IBatch'
import { SCENE } from 'src/applications/Embed/types/IScene'
import { useEmbedContinueFlow } from 'src/applications/Oversight/hooks/useEmbedContinueFlow'
import { ITeamWithCapabilities, TeamContext } from 'src/contexts/TeamContext'
import { updateClientLink } from 'src/resources/clients/graphClient'
import { TestEnvironment } from 'src/resources/layouts/SidebarLayout'
import { useSmartMutation } from 'src/smart/hooks/useSmartMutation'
import { SM_CANCEL_WORKBOOK } from 'src/smart/mutations/SM_CANCEL_WORKBOOK'
import { SM_DISMISS_BATCH } from 'src/smart/mutations/SM_DISMISS_BATCH'
import { SM_INIT_EMPTY_BATCH } from 'src/smart/mutations/SM_INIT_EMPTY_BATCH'
import { SM_PREFLIGHT_BATCH } from 'src/smart/mutations/SM_PREFLIGHT_BATCH'
import { useWorkbookUpdatedSubscriptionWithPollFallback } from 'src/smart/queries/SQ_WORKBOOK_UPDATED_SUBSCRIPTION'
import { detach } from 'src/utils/promise'
import styled from 'styled-components'

const ThemedTestEnvironment = styled(TestEnvironment)`
  border-top-color: ${themed('primary')};
  span {
    background: ${themed('primary')};
  }
`

const TeamContextWrapper = ({
  children,
  team,
  batch,
  onInit
}: {
  team: any
  batch: IBatch
  onInit: (theme: DefaultTheme) => void
  children: ReactNode
}) => {
  useEffect(() => {
    // sets language to default 'en' if feature flag is not enabled
    if (!batch.features.includes('EMBEDS_I18N')) {
      i18next.changeLanguage('en').then()
    }
  }, [batch])
  return (
    <TeamContext.Provider value={team as unknown as ITeamWithCapabilities}>
      <FeatureFlagContext.Provider value={batch.features ?? []}>
        <ThemeWrapper config={batch.config} onInit={onInit}>
          {children}
        </ThemeWrapper>
      </FeatureFlagContext.Provider>
    </TeamContext.Provider>
  )
}

export const EmbedController = () => {
  const [showNextAction, setShowNextAction] = useState(true)
  const [scene, setScene] = useState<SCENE>(SCENE.INITIAL)
  const [batch, setBatch] = useState<IBatch>(null)
  const [locale, setLocale] = useState('en')
  const [newWorkspaceId, setNewWorkspaceId] = useState<string>('')
  const [theme, setTheme] = useState<any>({})
  const { t } = useTranslation()
  const { addToast } = useToasts()
  const preflightBatch = useSmartMutation(SM_PREFLIGHT_BATCH)
  const dismissBatch = useSmartMutation(SM_DISMISS_BATCH)
  const cancelWorkbook = useSmartMutation(SM_CANCEL_WORKBOOK)
  const initializeEmptyBatch = useSmartMutation(SM_INIT_EMPTY_BATCH)

  const workbookUpdatedSubscription = useWorkbookUpdatedSubscriptionWithPollFallback(
    batch?.workbookId,
    newWorkspaceId || preflightBatch?.result?.workspaceId
  )

  const workbookStatus =
    workbookUpdatedSubscription.result?.status ||
    workbookUpdatedSubscription?.fallbackResult?.primaryWorkbook?.status

  const languageWhitelist = ['en', 'fr', 'es']

  const handleCancelBatch = async () => {
    const { newBatchId, workspaceId } = await dismissBatch
      .run({
        batchId: batch.batchId
      })
      .catch(async ({ graphQLErrors }) => {
        if (
          graphQLErrors.some(
            ({ message }: { message: string }) => message === 'This batch is already cancelled.'
          )
        ) {
          const { batchId, workspaceId } = await initializeEmptyBatch.run({
            importedFromUrl: location.href
          })

          return { newBatchId: batchId, workspaceId }
        }
      })
    setShowNextAction(false)
    setNewWorkspaceId(workspaceId)
    setScene(SCENE.INITIAL)
    setBatch(null)

    detach(
      preflightBatch.run({ batchId: newBatchId, locale: locale }).catch((e) => {
        console.error(e)
        setScene(null)
      })
    )
  }

  const shouldAutoContinue = useMemo(() => {
    return qs.parse(location.search)?.autoContinue === '1'
  }, [location.search])

  const handleCancelWorkbook = async () => {
    const { success } = await cancelWorkbook.run({
      workbookId: batch.workbookId
    })
    if (success) {
      setShowNextAction(false)
      setScene(SCENE.MATCH)
    } else {
      addToast(t('errors.toasts.cancelProcessing'), {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const embedContinueFlowModal = useEmbedContinueFlow({
    theme,
    onReset: handleCancelBatch
  })

  useEffect(() => {
    const baseLanguage = i18next.language.split('-')[0]
    if (languageWhitelist.includes(baseLanguage) && batch?.features?.includes('EMBEDS_I18N')) {
      setLocale(baseLanguage)
    } else {
      setLocale('en')
    }
  }, [batch])

  useEffect(() => {
    const { jwt, batchId } = qs.parse(location.search)

    if (typeof jwt !== 'string' || typeof batchId !== 'string') {
      setScene(null)
      return
    }

    updateClientLink(decodeURI(jwt.toString()))
    detach(
      preflightBatch.run({ batchId, locale }).catch((e) => {
        console.error(e)
        setScene(null)
      })
    )
  }, [locale])

  useEffect(() => {
    if (preflightBatch?.result) {
      const {
        batchId,
        schemas,
        status,
        teamName,
        teamId,
        uploadId,
        workbookId,
        workspaceId,
        config,
        features,
        paymentExpired,
        synced
      } = preflightBatch.result
      const [schema] = schemas
      setBatch({
        batchId,
        features,
        schema: schema.jsonSchema.schema,
        schemaId: schema.id,
        status,
        teamName,
        teamId,
        uploadId,
        workbookId,
        workspaceId: newWorkspaceId || workspaceId,
        config,
        paymentExpired,
        synced
      })

      // TODO: this entire set of logic is risky and relies on so much inference
      if (workbookId) {
        setScene(SCENE.REVIEW)
        if (!shouldAutoContinue) {
          embedContinueFlowModal.open()
        }
      } else if (uploadId) {
        if (!shouldAutoContinue) {
          embedContinueFlowModal.open()
        }
      } else {
        setShowNextAction(false)
      }
    }
  }, [preflightBatch?.result])

  const handleComplete = useCallback<(u?: Partial<IBatch>) => void>(
    async (update) => {
      if (update) {
        setBatch((b) => Object.assign({}, b, update))
      }

      switch (scene) {
        case SCENE.INITIAL:
        case SCENE.SOURCE:
        case SCENE.HEADER_CHECK:
        case SCENE.MATCH:
        case SCENE.REVIEW:
        case SCENE.FINAL: {
          setShowNextAction(true)
          break
        }
      }
    },
    [scene, batch]
  )

  const handleGoBack = useCallback((scene: SCENE) => {
    setScene(scene)
  }, [])

  // need paymentExpired info here
  if (!scene || batch?.paymentExpired) {
    batch?.paymentExpired ? console.error('Your trial has expired') : null
    return <ErrorScene />
  }
  // TODO: add some more loading nuance
  if (!batch || preflightBatch.state.loading || !batch.teamId) {
    if (batch?.config) {
      const team = { id: batch.teamId, name: batch.teamName }
      return (
        <TeamContextWrapper batch={batch} team={team} onInit={setTheme}>
          <Loader
            top={40}
            padding={0}
            message={
              preflightBatch.state.loading
                ? t('elements.loaders.initializing')
                : t('elements.loaders.processing') + '...'
            }
          />
        </TeamContextWrapper>
      )
    } else {
      return (
        <Loader
          top={40}
          padding={0}
          message={
            preflightBatch.state.loading
              ? t('elements.loaders.initializing')
              : t('elements.loaders.processing') + '...'
          }
        />
      )
    }
  }

  const team = { id: batch.teamId, name: batch.teamName }
  if (showNextAction) {
    return (
      <TeamContextWrapper batch={batch} team={team} onInit={setTheme}>
        {batch.config.devMode && (
          <ThemedTestEnvironment>
            <span>{t('miscEmbeds.devMode')}</span>
          </ThemedTestEnvironment>
        )}
        <EmbedNextAction
          batch={batch}
          onReset={handleCancelBatch}
          onNextAction={(nextScene, update) => {
            setShowNextAction(false)
            setScene(nextScene)
            setBatch((b) => Object.assign({}, b, update))
          }}
        />
      </TeamContextWrapper>
    )
  }

  const SceneComponent = {
    [SCENE.INITIAL]: InitialScene,
    [SCENE.HEADER_CHECK]: HeaderRowSelectionScene,
    [SCENE.SOURCE]: DataSourceScene,
    [SCENE.MATCH]: MatchingScene,
    [SCENE.REVIEW]: ReviewScene,
    [SCENE.FINAL]: FinalScene
  }[scene]

  return (
    <TeamContextWrapper batch={batch} team={team} onInit={setTheme}>
      {embedContinueFlowModal.render()}
      {batch.config.devMode && (
        <ThemedTestEnvironment>
          <span>{t('miscEmbeds.devMode')}</span>
        </ThemedTestEnvironment>
      )}
      <SceneComponent
        batch={batch}
        team={team}
        onComplete={handleComplete}
        onCancelBatch={handleCancelBatch}
        onCancelWorkbook={handleCancelWorkbook}
        onGoBack={handleGoBack}
        workbookStatus={workbookStatus}
        workspaceId={preflightBatch.result.workspaceId}
      />
    </TeamContextWrapper>
  )
}
